import { Box, TableCell, TableRow } from '@material-ui/core'
import { ChangeEvent, useCallback, useEffect, useMemo, useState, EventHandler, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { DonationMatchRequestStatusBadge } from '@percent/admin-dashboard/app/donationMatchRequest/donationMatchRequestStatusBadge/DonationMatchRequestStatusBadge'
import {
  DonationMatchRequestsStatusFilter,
  DonationMatchRequestsTableProps
} from '@percent/admin-dashboard/app/donationMatchRequest/donationMatchRequestsList/donationMatchRequestsTable/DonationMatchRequestsTable.types'
import { ErrorView, Table } from '@percent/admin-dashboard/common/components'
import { TableSort } from '@percent/admin-dashboard/common/components/table/Table.types'
import { useCurrencies, useFilterValue } from '@percent/admin-dashboard/common/hooks'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { SearchInput, Typography } from '@percent/lemonade'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import styles from './DonationMatchRequestTable.module.scss'

const cells = [
  { id: 'partner', isSortable: false, props: { width: '20%' } },
  { id: 'donationAmount', isSortable: false, props: { width: '20%' } },
  { id: 'organization', isSortable: false, props: { width: '20%' } },
  { id: 'status', isSortable: false, props: { width: '15%' } },
  { id: 'submitted', isSortable: true, props: { width: '20%' } }
]

const statusMap = new Map<DonationMatchRequestsStatusFilter, string[] | undefined>([
  [DonationMatchRequestsStatusFilter.PENDING, ['processing']],
  [DonationMatchRequestsStatusFilter.APPROVED, ['approved']],
  [DonationMatchRequestsStatusFilter.REJECTED, ['rejected']],
  [DonationMatchRequestsStatusFilter.VALIDATING, ['validating']],
  [DonationMatchRequestsStatusFilter.ALL, undefined] // No status filter = all results
])

const urlMap: Record<DonationMatchRequestsStatusFilter, string> = {
  [DonationMatchRequestsStatusFilter.PENDING]: '/donation-match-requests?status=processing',
  [DonationMatchRequestsStatusFilter.APPROVED]: '/donation-match-requests?status=approved',
  [DonationMatchRequestsStatusFilter.REJECTED]: '/donation-match-requests?status=rejected',
  [DonationMatchRequestsStatusFilter.VALIDATING]: '/donation-match-requests?status=validating',
  [DonationMatchRequestsStatusFilter.ALL]: '/donation-match-requests' // No status parameter = all results
}

export function DonationMatchRequestsTable({
  isLoading,
  totalResults,
  data,
  previousPage,
  nextPage,
  errorMessage,
  queryParams,
  setQueryParams
}: DonationMatchRequestsTableProps) {
  const { t } = useTranslation()
  const { currencyInfo } = useCurrencies()
  const location = useLocation()
  const navigate = useNavigate()

  const [active, setActive] = useState<string>('')
  const [searchValue, setSearchValue] = useFilterValue<string>(queryParams.query || '', [queryParams.query])

  // Helper function to find matching filter based on current URL status parameters
  const findMatchingFilter = useCallback((searchParams: URLSearchParams): DonationMatchRequestsStatusFilter => {
    const currentStatuses = searchParams.getAll('status').sort((a, b) => a.localeCompare(b))

    // Handle the case where no status is provided (ALL filter)
    if (currentStatuses.length === 0) {
      return DonationMatchRequestsStatusFilter.ALL
    }

    // Find the filter that matches the current status array
    for (const [filter, statuses] of statusMap.entries()) {
      if (
        statuses &&
        statuses.length === currentStatuses.length &&
        statuses.every(status => currentStatuses.includes(status))
      ) {
        return filter
      }
    }

    // If no exact match found, return ALL instead of PENDING
    return DonationMatchRequestsStatusFilter.ALL
  }, [])

  useEffect(() => {
    if (!location) return

    if (!location.search) {
      // Default to ALL tab instead of PENDING when no search parameters
      setActive(urlMap[DonationMatchRequestsStatusFilter.ALL])

      return
    }

    const searchParams = new URLSearchParams(location.search)
    const matchingFilter = findMatchingFilter(searchParams)
    const activeUrl = urlMap[matchingFilter]

    setActive(activeUrl)
  }, [location, findMatchingFilter])

  const items = useMemo(
    () => [
      { key: urlMap[DonationMatchRequestsStatusFilter.PENDING], label: t('table.pendingValidation') },
      { key: urlMap[DonationMatchRequestsStatusFilter.VALIDATING], label: t('table.verificationSubmitted') },
      { key: urlMap[DonationMatchRequestsStatusFilter.APPROVED], label: t('table.approved') },
      { key: urlMap[DonationMatchRequestsStatusFilter.REJECTED], label: t('table.rejected') },
      { key: urlMap[DonationMatchRequestsStatusFilter.ALL], label: t('status.all') }
    ],
    [t]
  )

  const onChangeTab = useCallback(
    (newValue: string) => {
      setActive(newValue)
      navigate(newValue)
    },
    [navigate]
  )

  const handleRequestSort = () =>
    setQueryParams({
      direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC'
    })

  const handleKeyChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    },
    [setSearchValue]
  )

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          ...queryParams,
          query: (event.target as HTMLInputElement).value
        })
      }

      if (event.key === 'Escape') {
        setSearchValue('')
      }

      return null
    },
    [queryParams, setQueryParams, setSearchValue]
  )

  const handleClearValue = useCallback(() => {
    setSearchValue('')
    setQueryParams({
      ...queryParams,
      query: undefined
    })
  }, [queryParams, setQueryParams, setSearchValue])

  const searchInput = useMemo(
    () => (
      <Box className={styles.searchWrapper}>
        <SearchInput
          value={searchValue ?? ''}
          handleClearValue={handleClearValue}
          onKeyDown={handleKeyPress}
          onChange={handleKeyChange}
          placeholder={t('table.matchRequestSearchPlaceholder')}
        />
      </Box>
    ),
    [searchValue, handleClearValue, handleKeyPress, handleKeyChange, t]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Box className={styles.tableWrapper}>
      <Table
        isLoading={isLoading}
        totalResults={totalResults}
        data={data}
        nextPage={nextPage}
        previousPage={previousPage}
        columns={cells}
        orderBy="submitted"
        items={items}
        active={active}
        onChangeTab={onChangeTab}
        handleRequestSort={handleRequestSort}
        order={(queryParams.direction?.toLowerCase() as TableSort) ?? 'desc'}
        emptyTableText={t('table.emptyDonationMatchRequests')}
        filtersContent={searchInput}
      >
        {data?.map(row => {
          return (
            <TableRow
              key={row.id}
              className={styles.tableRow}
              onClick={handleClickAsLink(`/donation-match-requests/${row.id}`, navigate)}
            >
              <TableCell>{row.partner.name}</TableCell>
              <TableCell>
                {formatAmountFromMinorUnits({
                  currencyCode: row.amount.currency,
                  value: row.amount.amount,
                  currencies: currencyInfo
                })}
              </TableCell>
              <TableCell>{row?.organisation?.name ?? row?.organisationDetails?.name}</TableCell>
              <TableCell>
                <DonationMatchRequestStatusBadge status={row.status} />
              </TableCell>
              <TableCell>{dayJS(row.createdAt).format('DD MMM YYYY, HH:mm')}</TableCell>
              <TableCell align="right" style={{ padding: '16px 0' }}>
                <Typography className={styles.dummyLink} variant="default">
                  {t('table.seeDetails')}
                </Typography>
              </TableCell>
            </TableRow>
          )
        })}
      </Table>
    </Box>
  )
}
